/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Button, FormField, FormSelect, Toaster } from '../../components';
import FormTextArea from '../../components/custom/form-text-area';

import { ICusSupportFormData, SupportFormOption } from './cus-support-form.data';
import { ICusSupportFormProps } from './cus-support-form.props.autogenerated';
import { useSupportForm, useSupportFormRequests } from './hooks';

export interface ICusSupportFormViewProps extends ICusSupportFormProps<ICusSupportFormData> {}

export default function CusSupportForm(props: ICusSupportFormViewProps) {
    const { config, context } = props;
    if (!config.supportEmail || !config.supportSubject || !config.itemsLabels) {
        return;
    }
    const { formData, updateFormData, selectOptions, errors, isValid, clearFormData } = useSupportForm(props.config);
    const { submitSupportForm, setIsToasterOpen, isToasterOpen, isFormLoading, isFormSubmitted } = useSupportFormRequests(
        formData,
        context,
        config.supportEmail,
        config.supportSubject,
        config.itemsLabels,
        clearFormData,
        context
    );

    return !isFormSubmitted ? (
        <form className='bs-form'>
            <h1 className='bs-form__title'>{config.formTitle}</h1>

            <FormSelect
                label={config.topicField.label}
                options={selectOptions || []}
                value={formData.topic.toString()}
                onChange={e => updateFormData('topic', +e.target.value)}
                error={errors['topic'] as string | undefined}
            />

            <div className='bs-form__row'>
                <FormField
                    label={config.nameField?.label}
                    placeholder={config.nameField?.placeholder}
                    value={formData.name}
                    onChange={e => updateFormData('name', e.target.value)}
                    error={errors['name'] as string | undefined}
                    name={config.nameField?.label}
                />
                <FormField
                    label={config.emailField?.label}
                    placeholder={config.emailField?.placeholder}
                    value={formData.email}
                    onChange={e => updateFormData('email', e.target.value)}
                    error={errors['email'] as string | undefined}
                    inputType='email'
                    autocomplete='email'
                    name={config.emailField?.label}
                />
            </div>

            <div className='bs-form__row'>
                <FormField
                    label={config.phoneNumber?.label}
                    placeholder={config.phoneNumber?.placeholder}
                    value={formData.phoneNumber}
                    onChange={e => updateFormData('phoneNumber', e.target.value)}
                    error={errors['phoneNumber'] as string | undefined}
                    inputType='tel'
                    autocomplete='tel'
                    name={config.phoneNumber?.label}
                />
            </div>

            <div className='bs-form__row'>
                <FormTextArea
                    label={config.descriptionField?.label}
                    placeholder={config.descriptionField?.placeholder}
                    value={formData.description}
                    onChange={e => updateFormData('description', e.target.value)}
                    error={errors['description'] as string | undefined}
                    explainerText={config.descriptionField?.explainerText}
                />
            </div>

            {[
                SupportFormOption.MissingItem,
                SupportFormOption.OrderNotReceived,
                SupportFormOption.WrongSizeColor,
                SupportFormOption.DamagedItem,
                SupportFormOption.Other
            ].includes(formData.topic) && (
                <fieldset>
                    {/* <legend>
                        <h2 className='bs-form__title'>{config.formDetailsSection}</h2>
                    </legend> */}

                    <div className='bs-form__row'>
                        <FormField
                            label={config.orderNumberField?.label}
                            placeholder={config.orderNumberField?.placeholder}
                            value={formData.orderNumber}
                            onChange={e => updateFormData('orderNumber', e.target.value)}
                            error={errors['orderNumber'] as string | undefined}
                            name={config.orderNumberField?.label}
                        />
                    </div>
                    {/*REMOVED UNTIL WE FIGURE OUT HOW TO STYLE IT*/}
                    {/*{[SupportFormOption.MissingItem, SupportFormOption.WrongSizeColor, SupportFormOption.DamagedItem].includes(*/}
                    {/*    formData.topic*/}
                    {/*) && (*/}
                    {/*    <div className='bs-form__row'>*/}
                    {/*        <ItemPropertiesFields*/}
                    {/*            config={config}*/}
                    {/*            formData={formData}*/}
                    {/*            errors={errors}*/}
                    {/*            updateFormItemData={updateFormItemData}*/}
                    {/*            updateFormData={updateFormData}*/}
                    {/*        />*/}
                    {/*    </div>*/}
                    {/*)}*/}
                </fieldset>
            )}
            <Button onClick={submitSupportForm} disabled={!isValid} isLoading={isFormLoading}>
                {config.submitButton.label}
            </Button>

            {isToasterOpen && <Toaster type='success' message={props.config.feedbackMessage} onClose={() => setIsToasterOpen(false)} />}
        </form>
    ) : (
        <section className='bs-support-success'>
            <div className='bs-support-success__card'>
                <div className='bs-support-success__icon' aria-hidden='true'>
                    ✓
                </div>

                <p className='bs-support-success__message'>{props.config.feedbackExtendedMessage}</p>
            </div>
        </section>
    );
}
